import { axiosInstanceWhatsAppConfirmation } from "../instance";

export type AppointmentSellerStatus = "active" | "inactive" | "disconnected";
export type AppointmentFlowStatus = "draft" | "active" | "paused" | "archived";
export type AppointmentBookingMode = "qualify_then_book" | "prebook_then_qualify";
export type AppointmentRejectionAction = "politely_decline" | "sell_product";
export type AppointmentPriceDisclosurePolicy = "show_when_asked" | "hide_until_meeting";
export type AppointmentCaptureConsentMode = "notice" | "explicit_ack";
export type AppointmentReportsVerificationStatus =
  | "not_configured"
  | "pending"
  | "verified"
  | "present_unverified"
  | "unavailable"
  | "ambiguous"
  | "error";
export type AppointmentCapabilityReadinessStatus =
  | "unknown"
  | "checking"
  | "verified"
  | "unavailable"
  | "error";
export type AppointmentMeetingCreationStatus =
  | "unknown"
  | "pending"
  | "success"
  | "failure";
export type AppointmentMeetingConfigurationStatus =
  | "unknown"
  | "pending"
  | "verified"
  | "unavailable"
  | "error";
export type AppointmentQualificationResult = "unknown" | "pass" | "fail";
export type AppointmentQualificationStatus =
  | "not_started"
  | "in_progress"
  | "qualified"
  | "disqualified"
  | "manual_review";
export type AppointmentBookingStatus = "prebooked" | "confirmed" | "cancelled" | "completed" | "no_show";
export type AppointmentSyncStatus = "pending" | "synced" | "reconcile" | "error";
export type AppointmentGoogleRole = "seller" | "workspace_admin";
export type AppointmentGoogleGrantLevel = "scheduling" | "capture";
export type AppointmentCapturePreflightStatus =
  | "queued"
  | "awaiting_seller"
  | "in_meeting"
  | "processing_artifacts"
  | "verified"
  | "failed"
  | "expired"
  | string;

export interface AppointmentSeller {
  id: number;
  display_name: string;
  email: string;
  timezone: string;
  status: AppointmentSellerStatus | string;
  meeting_duration_minutes: number;
  buffer_before_minutes: number;
  buffer_after_minutes: number;
  max_daily_meetings: number | null;
  round_robin_weight: number;
  created_at: string;
  updated_at: string;
}

export interface AppointmentSellerCreate {
  display_name: string;
  email: string;
  timezone?: string;
  status?: AppointmentSellerStatus;
  meeting_duration_minutes?: number;
  buffer_before_minutes?: number;
  buffer_after_minutes?: number;
  max_daily_meetings?: number | null;
  round_robin_weight?: number;
}

export type AppointmentSellerPatch = Partial<AppointmentSellerCreate>;

export interface AppointmentSellerCalendar {
  id: number;
  seller_id: number;
  connection_id: number;
  google_calendar_id: string;
  display_name: string | null;
  timezone: string | null;
  is_write_calendar: boolean;
  blocks_availability: boolean;
  status: "active" | "inactive" | "error" | string;
  created_at: string;
  updated_at: string;
}

export interface AppointmentSellerCalendarCreate {
  connection_id: number;
  google_calendar_id: string;
  display_name?: string | null;
  timezone?: string | null;
  is_write_calendar?: boolean;
  blocks_availability?: boolean;
}

export interface AppointmentSellerCalendarPatch {
  display_name?: string | null;
  timezone?: string | null;
  is_write_calendar?: boolean;
  blocks_availability?: boolean;
  status?: "active" | "inactive" | "error";
}

export type AppointmentQualificationQuestion = Record<string, unknown>;

export interface AppointmentInitialNudgeAutomation {
  enabled: boolean;
  delay_minutes: number;
  template_name: string | null;
  language: string;
}

export interface AppointmentReminderAutomation {
  enabled: boolean;
  minutes_before: number;
  template_name: string;
  language: string;
}

export interface AppointmentNoShowRecoveryAutomation {
  enabled: boolean;
  delay_minutes: number;
  template_name: string | null;
  language: string;
}

export interface AppointmentAutomationSettings {
  initial_nudge: AppointmentInitialNudgeAutomation;
  reminders: AppointmentReminderAutomation[];
  no_show_recovery: AppointmentNoShowRecoveryAutomation;
}

export interface AppointmentFlow {
  id: number;
  name: string;
  public_aliases: string[];
  status: AppointmentFlowStatus | string;
  timezone: string;
  booking_mode: AppointmentBookingMode | string;
  meeting_duration_minutes: number;
  attendance_grace_minutes: number;
  minimum_notice_minutes: number;
  booking_window_days: number;
  hold_ttl_seconds: number;
  financial_criterion_enabled: boolean;
  financial_criterion: string | null;
  awareness_criterion_enabled: boolean;
  awareness_criterion: string | null;
  fit_criterion_enabled: boolean;
  fit_criterion: string | null;
  qualification_instructions: string | null;
  qualification_questions_json: unknown[];
  rejection_action: AppointmentRejectionAction | string;
  rejection_funnel_id: string | null;
  communicable_price: string | number | null;
  communicable_price_currency: string | null;
  hide_price_until_meeting: boolean;
  price_disclosure_policy: AppointmentPriceDisclosurePolicy;
  capture_notice_text: string | null;
  capture_consent_mode: AppointmentCaptureConsentMode;
  artifact_retention_days: number;
  automations: AppointmentAutomationSettings;
  created_at: string;
  updated_at: string;
}

export interface AppointmentFlowCreate {
  name: string;
  public_aliases?: string[];
  timezone?: string;
  status?: AppointmentFlowStatus;
  booking_mode?: AppointmentBookingMode;
  meeting_duration_minutes?: number;
  attendance_grace_minutes?: number;
  minimum_notice_minutes?: number;
  booking_window_days?: number;
  hold_ttl_seconds?: number;
  financial_criterion_enabled?: boolean;
  financial_criterion?: string | null;
  awareness_criterion_enabled?: boolean;
  awareness_criterion?: string | null;
  fit_criterion_enabled?: boolean;
  fit_criterion?: string | null;
  qualification_instructions?: string | null;
  qualification_questions_json?: unknown[];
  rejection_action?: AppointmentRejectionAction;
  rejection_funnel_id?: string | null;
  communicable_price?: number | null;
  communicable_price_currency?: string | null;
  hide_price_until_meeting?: boolean;
  price_disclosure_policy?: AppointmentPriceDisclosurePolicy;
  capture_notice_text?: string | null;
  capture_consent_mode?: AppointmentCaptureConsentMode;
  artifact_retention_days?: number;
  automations?: AppointmentAutomationSettings;
}

export type AppointmentFlowPatch = Partial<AppointmentFlowCreate>;

export interface AppointmentAvailabilityRule {
  id: number;
  seller_id: number;
  flow_id: number | null;
  day_of_week: number;
  start_local_time: string;
  end_local_time: string;
  spans_midnight: boolean;
  timezone: string;
  valid_from: string | null;
  valid_until: string | null;
  enabled: boolean;
  priority: number;
  created_at: string;
  updated_at: string;
}

export interface AppointmentAvailabilityRuleCreate {
  seller_id: number;
  flow_id?: number | null;
  day_of_week: number;
  start_local_time: string;
  end_local_time: string;
  spans_midnight?: boolean;
  timezone?: string;
  valid_from?: string | null;
  valid_until?: string | null;
  enabled?: boolean;
  priority?: number;
}

export type AppointmentAvailabilityRulePatch = Partial<
  Omit<AppointmentAvailabilityRuleCreate, "seller_id" | "flow_id">
>;

export interface AppointmentSummary {
  id: number;
  flow_id: number;
  lead_intent_id: number | null;
  seller_id: number;
  qualification_status: AppointmentQualificationStatus | string;
  booking_status: AppointmentBookingStatus | string;
  sync_status: AppointmentSyncStatus | string;
  starts_at: string;
  ends_at: string;
  timezone: string;
  attendee_name: string | null;
  attendee_email: string | null;
  attendee_phone: string | null;
  version: number;
  confirmed_at: string | null;
  cancelled_at: string | null;
  completed_at: string | null;
  attendee_attendance_status: AppointmentAttendanceStatus;
  seller_attendance_status: AppointmentAttendanceStatus;
  attendance_source: AppointmentAttendanceSource | null;
  outcome_recorded_at: string | null;
  attendance_grace_minutes_snapshot: number;
  absence_eligible_at: string;
  created_at: string;
  updated_at: string;
}

export type AppointmentAttendanceStatus = "unknown" | "present" | "absent";
export type AppointmentAttendanceSource =
  | "manual"
  | "google_meet"
  | "google_workspace_reports"
  | "mixed";

export interface AppointmentOutcomeMutation {
  expected_version: number;
  attendee_status: AppointmentAttendanceStatus;
  seller_status: AppointmentAttendanceStatus;
  notes?: string | null;
}

export interface AppointmentDetail extends AppointmentSummary {
  person_id: number | null;
  contact_id: number | null;
  conversation_id: number | null;
  qualification_summary: string | null;
  qualification_reason: string | null;
  qualification_snapshot: Record<string, unknown>;
  meeting_briefing: Record<string, unknown>;
  outcome_recorded_by: string | null;
  outcome_notes: string | null;
  outcome_evidence: Record<string, unknown>;
  calendar: AppointmentCalendarDetail | null;
}

export interface AppointmentCalendarDetail {
  google_event_status: string | null;
  sync_status: string | null;
  event_html_link: string | null;
  hangout_link: string | null;
  meet_code: string | null;
  attendee_response_status: string | null;
  remote_updated_at: string | null;
}

export type AppointmentCaptureConsentStatus =
  | "not_captured"
  | "pending"
  | "accepted"
  | "declined";
export type AppointmentMeetingIdentityAssurance =
  | "unverified"
  | "observed"
  | "matched"
  | "verified"
  | "ambiguous";
export type AppointmentMeetingArtifactStatus =
  | "unknown"
  | "waiting_for_conference"
  | "started"
  | "ended"
  | "file_generated"
  | "not_observed_after_deadline"
  | "unavailable"
  | "error";

export interface AppointmentMeetingCapturePolicy {
  consent_mode: AppointmentCaptureConsentMode | null;
  consent_status: AppointmentCaptureConsentStatus;
  notice_text: string | null;
  artifact_retention_days: number | null;
  consent_source: "booking_page" | "whatsapp" | "manual" | null;
  consent_at: string | null;
}

export interface AppointmentMeetingBinding {
  meet_code: string | null;
  meet_space_name: string | null;
  creation_status: AppointmentMeetingCreationStatus;
  configuration_status: AppointmentMeetingConfigurationStatus;
  settings_snapshot: Record<string, unknown>;
  configured_at: string | null;
  configuration_error: string | null;
}

export interface AppointmentMeetingSession {
  session_name: string;
  started_at: string | null;
  ended_at: string | null;
}

export interface AppointmentMeetingParticipant {
  participant_name: string;
  participant_kind: "signed_in" | "anonymous" | "phone";
  meet_user_id: string | null;
  display_name: string | null;
  matched_role: "unknown" | "seller" | "attendee";
  identity_assurance: AppointmentMeetingIdentityAssurance;
  identity_reason: string | null;
  first_observed_at: string | null;
  last_observed_at: string | null;
  duration_seconds: number | null;
  active: boolean;
  sessions: AppointmentMeetingSession[];
}

export interface AppointmentMeetingArtifact {
  artifact_name: string;
  artifact_type: "recording" | "transcript" | "smart_notes";
  status: AppointmentMeetingArtifactStatus;
  started_at: string | null;
  ended_at: string | null;
  export_uri: string | null;
  access_status: "unknown" | "available" | "permission_denied" | "deleted" | "unavailable";
  last_observed_at: string | null;
  error: string | null;
}

export interface AppointmentMeetingConference {
  conference_record_name: string;
  meet_space_name: string | null;
  meeting_code: string | null;
  started_at: string | null;
  ended_at: string | null;
  match_status: "candidate" | "verified" | "rejected";
  match_reason: string | null;
  is_selected: boolean;
  attendance_status: "unknown" | "pending" | "observed" | "unavailable" | "error";
  recording_status: AppointmentMeetingArtifactStatus | string;
  transcription_status: AppointmentMeetingArtifactStatus | string;
  smart_notes_status: AppointmentMeetingArtifactStatus | string;
  last_observed_at: string | null;
  participants: AppointmentMeetingParticipant[];
  artifacts: AppointmentMeetingArtifact[];
}

export interface AppointmentMeetingDetail {
  appointment_id: number;
  source: "google_meet_api";
  identity_verification_status: AppointmentReportsVerificationStatus;
  identity_verification_reason: string | null;
  identity_verification_source: "google_workspace_reports";
  identity_verified_at: string | null;
  identity_last_observed_at: string | null;
  verified_attendee_email: string | null;
  recording_playback_status: "unavailable";
  capture_policy: AppointmentMeetingCapturePolicy;
  binding: AppointmentMeetingBinding | null;
  conferences: AppointmentMeetingConference[];
  last_observed_at: string | null;
}

export interface AppointmentTranscriptSpeaker {
  display_name: string | null;
  matched_role: "unknown" | "seller" | "attendee";
  identity_assurance: AppointmentMeetingIdentityAssurance;
}

export interface AppointmentTranscriptEntry {
  sequence_number: number;
  text: string;
  text_truncated: boolean;
  truncated_tail_retrievable: boolean | null;
  original_character_count: number;
  language_code: string | null;
  started_at: string | null;
  ended_at: string | null;
  retained_until: string | null;
  speaker: AppointmentTranscriptSpeaker | null;
}

export interface AppointmentTranscriptResponse {
  appointment_id: number;
  source: "google_meet_api";
  identity_verification_status: "unavailable";
  entries: AppointmentTranscriptEntry[];
  limit: number;
  offset: number;
  has_more: boolean;
  next_offset: number | null;
  returned_character_count: number;
  max_character_budget: number;
}

export interface AppointmentMeetRefreshJob {
  job_type: "configure_space" | "discover_conferences" | "sync_attendance" | "sync_artifacts" | "verify_reports";
  status: "pending" | "running" | "retry_wait" | "succeeded" | "failed" | "cancelled";
  available_at: string;
  deadline_at: string | null;
}

export interface AppointmentMeetRefreshResponse {
  appointment_id: number;
  status: "queued";
  requested_at: string;
  jobs: AppointmentMeetRefreshJob[];
}

export interface AppointmentListResponse {
  appointments: AppointmentSummary[];
  limit: number;
  offset: number;
}

export interface AppointmentRescheduleSlot {
  seller_id: number;
  starts_at: string;
  ends_at: string;
  timezone: string;
}

export interface AppointmentRescheduleSlotListResponse {
  slots: AppointmentRescheduleSlot[];
  total_slots: number;
  returned: number;
  has_more: boolean;
  starts_at_from: string;
  starts_at_to: string;
}

export interface AppointmentQualificationDecision {
  financial_result: AppointmentQualificationResult;
  awareness_result: AppointmentQualificationResult;
  fit_result: AppointmentQualificationResult;
  summary: string;
  reason: string;
  snapshot: Record<string, unknown>;
  briefing: Record<string, unknown>;
}

export interface AppointmentQualificationMutation {
  expected_version: number;
  decision: AppointmentQualificationDecision;
  capture_consent_accepted?: boolean | null;
}

export interface AppointmentPreparedOperation {
  id: number;
  appointment_id: number;
  appointment_revision: number | null;
  operation_type: string;
  status: string;
  attempts: number;
  version: number;
  available_at: string;
  created_at: string;
}

export interface AppointmentLifecycleMutationResponse {
  appointment: AppointmentDetail;
  operation: AppointmentPreparedOperation;
}

export interface AppointmentGoogleAuthorization {
  authorization_url: string;
  role: AppointmentGoogleRole;
  seller_id: number | null;
  grant_level: AppointmentGoogleGrantLevel | null;
  expires_at: string;
}

export interface AppointmentGoogleConnection {
  id: number;
  role: AppointmentGoogleRole;
  seller_id: number | null;
  google_email: string;
  hosted_domain: string | null;
  granted_scopes: string[];
  status: string;
  readiness_status: "verified_attendance_ready" | "capture_ready" | "scheduling_ready" | "unverified" | string;
  scheduling_ready: boolean;
  capture_scope_granted: boolean;
  capture_ready: boolean;
  verified_attendance_ready: boolean;
  meet_settings_scope_granted: boolean;
  meet_artifacts_read_scope_granted: boolean;
  /** Optional during the backend rollout; absence must be treated as not ready. */
  meet_space_create_scope_granted?: boolean;
  drive_meet_download_scope_granted: boolean;
  /** Authoritative routing readiness. Optional only for compatibility with the previous API. */
  capture_routable?: boolean;
  admin_reports_scope_ready: boolean;
  people_resource_name: string | null;
  meet_user_id: string | null;
  meet_identity_ready: boolean;
  meet_settings_status: AppointmentCapabilityReadinessStatus;
  recording_readiness_status: AppointmentCapabilityReadinessStatus;
  transcription_readiness_status: AppointmentCapabilityReadinessStatus;
  smart_notes_readiness_status: AppointmentCapabilityReadinessStatus;
  storage_readiness_status: AppointmentCapabilityReadinessStatus;
  capabilities_verified_at: string | null;
  capability_error: string | null;
  version: number;
  last_error: string | null;
  last_validated_at: string | null;
  revoked_at: string | null;
}

export interface AppointmentCapturePreflight {
  id: number;
  connection_id?: number;
  seller_id?: number;
  status: AppointmentCapturePreflightStatus;
  join_url: string | null;
  join_deadline_at: string | null;
  artifact_deadline_at: string | null;
  retryable: boolean;
  error: string | null;
  created_at?: string;
  updated_at?: string;
  completed_at?: string | null;
}

type AppointmentCapturePreflightApiResponse =
  | AppointmentCapturePreflight
  | { preflight: AppointmentCapturePreflight | null }
  | null;

const unwrapCapturePreflight = (
  payload: AppointmentCapturePreflightApiResponse
): AppointmentCapturePreflight | null => {
  if (payload == null) return null;
  return "preflight" in payload ? payload.preflight : payload;
};

export interface AppointmentListFilters {
  starts_at_from: string;
  starts_at_to: string;
  seller_id?: number;
  qualification_status?: AppointmentQualificationStatus;
  booking_status?: AppointmentBookingStatus;
  sync_status?: AppointmentSyncStatus;
  limit?: number;
  offset?: number;
}

const compactParams = (params: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );

const idempotencyHeaders = (idempotencyKey: string) => ({
  headers: { "Idempotency-Key": idempotencyKey },
});

export const appointmentSchedulingService = {
  async listSellers(): Promise<AppointmentSeller[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/appointments/sellers");
    return response.data.sellers;
  },

  async createSeller(payload: AppointmentSellerCreate): Promise<AppointmentSeller> {
    const response = await axiosInstanceWhatsAppConfirmation.post("/admin/appointments/sellers", payload);
    return response.data;
  },

  async updateSeller(sellerId: number, payload: AppointmentSellerPatch): Promise<AppointmentSeller> {
    const response = await axiosInstanceWhatsAppConfirmation.patch(
      `/admin/appointments/sellers/${sellerId}`,
      payload
    );
    return response.data;
  },

  async deleteSeller(sellerId: number): Promise<AppointmentSeller> {
    const response = await axiosInstanceWhatsAppConfirmation.delete(`/admin/appointments/sellers/${sellerId}`);
    return response.data;
  },

  async listSellerCalendars(sellerId: number): Promise<AppointmentSellerCalendar[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      `/admin/appointments/sellers/${sellerId}/calendars`
    );
    return response.data.calendars;
  },

  async createSellerCalendar(
    sellerId: number,
    payload: AppointmentSellerCalendarCreate
  ): Promise<AppointmentSellerCalendar> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/appointments/sellers/${sellerId}/calendars`,
      payload
    );
    return response.data;
  },

  async updateSellerCalendar(
    sellerCalendarId: number,
    payload: AppointmentSellerCalendarPatch
  ): Promise<AppointmentSellerCalendar> {
    const response = await axiosInstanceWhatsAppConfirmation.patch(
      `/admin/appointments/seller-calendars/${sellerCalendarId}`,
      payload
    );
    return response.data;
  },

  async deleteSellerCalendar(sellerCalendarId: number): Promise<AppointmentSellerCalendar> {
    const response = await axiosInstanceWhatsAppConfirmation.delete(
      `/admin/appointments/seller-calendars/${sellerCalendarId}`
    );
    return response.data;
  },

  async listFlows(): Promise<AppointmentFlow[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/appointments/flows");
    return response.data.flows;
  },

  async createFlow(payload: AppointmentFlowCreate): Promise<AppointmentFlow> {
    const response = await axiosInstanceWhatsAppConfirmation.post("/admin/appointments/flows", payload);
    return response.data;
  },

  async updateFlow(flowId: number, payload: AppointmentFlowPatch): Promise<AppointmentFlow> {
    const response = await axiosInstanceWhatsAppConfirmation.patch(`/admin/appointments/flows/${flowId}`, payload);
    return response.data;
  },

  async deleteFlow(flowId: number): Promise<AppointmentFlow> {
    const response = await axiosInstanceWhatsAppConfirmation.delete(`/admin/appointments/flows/${flowId}`);
    return response.data;
  },

  async listAvailabilityRules(params: { seller_id?: number; flow_id?: number } = {}): Promise<AppointmentAvailabilityRule[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/appointments/availability-rules", {
      params: compactParams(params),
    });
    return response.data.rules;
  },

  async createAvailabilityRule(
    payload: AppointmentAvailabilityRuleCreate
  ): Promise<AppointmentAvailabilityRule> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      "/admin/appointments/availability-rules",
      payload
    );
    return response.data;
  },

  async updateAvailabilityRule(
    ruleId: number,
    payload: AppointmentAvailabilityRulePatch
  ): Promise<AppointmentAvailabilityRule> {
    const response = await axiosInstanceWhatsAppConfirmation.patch(
      `/admin/appointments/availability-rules/${ruleId}`,
      payload
    );
    return response.data;
  },

  async deleteAvailabilityRule(ruleId: number): Promise<AppointmentAvailabilityRule> {
    const response = await axiosInstanceWhatsAppConfirmation.delete(
      `/admin/appointments/availability-rules/${ruleId}`
    );
    return response.data;
  },

  async listAppointments(filters: AppointmentListFilters): Promise<AppointmentListResponse> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/appointments/appointments", {
      params: compactParams(filters as unknown as Record<string, unknown>),
    });
    return response.data;
  },

  async getAppointment(appointmentId: number): Promise<AppointmentDetail> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      `/admin/appointments/appointments/${appointmentId}`
    );
    return response.data;
  },

  async listAppointmentRescheduleSlots(
    appointmentId: number,
    params: { starts_at_from: string; starts_at_to: string }
  ): Promise<AppointmentRescheduleSlotListResponse> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      `/admin/appointments/appointments/${appointmentId}/reschedule-slots`,
      { params }
    );
    return response.data;
  },

  async getAppointmentMeeting(appointmentId: number): Promise<AppointmentMeetingDetail> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      `/admin/appointments/appointments/${appointmentId}/meeting`
    );
    return response.data;
  },

  async getAppointmentTranscript(
    appointmentId: number,
    params: { limit?: number; offset?: number } = {}
  ): Promise<AppointmentTranscriptResponse> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      `/admin/appointments/appointments/${appointmentId}/transcript`,
      { params: compactParams(params) }
    );
    return response.data;
  },

  async refreshAppointmentMeeting(appointmentId: number): Promise<AppointmentMeetRefreshResponse> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/appointments/appointments/${appointmentId}/meeting/refresh`
    );
    return response.data;
  },

  async confirmAppointment(
    appointmentId: number,
    payload: AppointmentQualificationMutation,
    idempotencyKey: string
  ): Promise<AppointmentLifecycleMutationResponse> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/appointments/appointments/${appointmentId}/confirm`,
      payload,
      idempotencyHeaders(idempotencyKey)
    );
    return response.data;
  },

  async rejectAppointment(
    appointmentId: number,
    payload: AppointmentQualificationMutation,
    idempotencyKey: string
  ): Promise<AppointmentLifecycleMutationResponse> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/appointments/appointments/${appointmentId}/reject`,
      payload,
      idempotencyHeaders(idempotencyKey)
    );
    return response.data;
  },

  async rescheduleAppointment(
    appointmentId: number,
    payload: { expected_version: number; seller_id: number; starts_at: string; ends_at: string },
    idempotencyKey: string
  ): Promise<AppointmentLifecycleMutationResponse> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/appointments/appointments/${appointmentId}/reschedule`,
      payload,
      idempotencyHeaders(idempotencyKey)
    );
    return response.data;
  },

  async cancelAppointment(
    appointmentId: number,
    payload: { expected_version: number; reason: string },
    idempotencyKey: string
  ): Promise<AppointmentLifecycleMutationResponse> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/appointments/appointments/${appointmentId}/cancel`,
      payload,
      idempotencyHeaders(idempotencyKey)
    );
    return response.data;
  },

  async recordAppointmentOutcome(
    appointmentId: number,
    payload: AppointmentOutcomeMutation,
    idempotencyKey: string
  ): Promise<AppointmentDetail> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/appointments/appointments/${appointmentId}/outcome`,
      payload,
      idempotencyHeaders(idempotencyKey)
    );
    return response.data;
  },

  async authorizeSellerGoogle(payload: {
    seller_id: number;
    expected_email: string;
    grant_level?: AppointmentGoogleGrantLevel;
  }): Promise<AppointmentGoogleAuthorization> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      "/admin/appointments/google/seller/authorize",
      payload
    );
    return response.data;
  },

  async authorizeWorkspaceAdminGoogle(payload: {
    expected_email?: string | null;
  }): Promise<AppointmentGoogleAuthorization> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      "/admin/appointments/google/workspace-admin/authorize",
      payload
    );
    return response.data;
  },

  async listGoogleConnections(): Promise<AppointmentGoogleConnection[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/appointments/google/connections");
    return response.data.connections;
  },

  async getGoogleConnectionReadiness(connectionId: number): Promise<AppointmentGoogleConnection> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      `/admin/appointments/google/connections/${connectionId}/readiness`
    );
    return response.data;
  },

  async probeGoogleConnection(connectionId: number): Promise<AppointmentGoogleConnection> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/appointments/google/connections/${connectionId}/probe`
    );
    return response.data;
  },

  async reconnectGoogleConnection(
    connectionId: number,
    payload?: { grant_level?: AppointmentGoogleGrantLevel }
  ): Promise<AppointmentGoogleAuthorization> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/appointments/google/connections/${connectionId}/reconnect`,
      payload ?? {}
    );
    return response.data;
  },

  async startGoogleCapturePreflight(connectionId: number): Promise<AppointmentCapturePreflight | null> {
    const response = await axiosInstanceWhatsAppConfirmation.post<AppointmentCapturePreflightApiResponse>(
      `/admin/appointments/google/connections/${connectionId}/capture-preflight`,
      {}
    );
    return unwrapCapturePreflight(response.data);
  },

  async getGoogleCapturePreflight(connectionId: number): Promise<AppointmentCapturePreflight | null> {
    const response = await axiosInstanceWhatsAppConfirmation.get<AppointmentCapturePreflightApiResponse>(
      `/admin/appointments/google/connections/${connectionId}/capture-preflight`
    );
    return unwrapCapturePreflight(response.data);
  },

  async revokeGoogleConnection(connectionId: number): Promise<AppointmentGoogleConnection> {
    const response = await axiosInstanceWhatsAppConfirmation.delete(
      `/admin/appointments/google/connections/${connectionId}`
    );
    return response.data;
  },
};
